import { FC, useEffect, useRef } from "react";
import "./index.css";

const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const MAX_CONTENT_LENGTH = 8 * 1024 * 1024;

interface RichTextEditorProps {
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onError?: (message: string) => void;
  placeholder: string;
  value: string;
}

const RichTextEditor: FC<RichTextEditorProps> = ({
  ariaLabel,
  className = "",
  disabled = false,
  onChange,
  onError,
  placeholder,
  value,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  function emitChange() {
    onChange(editorRef.current?.innerHTML || "");
  }

  function appendImage(src: string) {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    const nextLength = editor.innerHTML.length + src.length;
    if (nextLength > MAX_CONTENT_LENGTH) {
      onError?.("项目需求内容过大，请减少粘贴图片后重试");
      return;
    }
    const image = document.createElement("img");
    image.src = src;
    image.alt = "项目需求图片";
    editor.appendChild(image);
    editor.appendChild(document.createElement("br"));
    emitChange();
  }

  function handlePaste(event: React.ClipboardEvent<HTMLDivElement>) {
    const imageFiles = Array.from(event.clipboardData.files || []).filter(
      (file) => file.type.startsWith("image/")
    );
    if (!imageFiles.length) {
      return;
    }
    event.preventDefault();
    imageFiles.forEach((file) => {
      if (file.size > MAX_IMAGE_SIZE) {
        onError?.(`图片 ${file.name || "未命名图片"} 超过 2MB，无法粘贴`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => appendImage(String(reader.result || ""));
      reader.onerror = () => onError?.("图片读取失败，请重新粘贴");
      reader.readAsDataURL(file);
    });
  }

  return (
    <div className="richTextEditorShell">
      <div
        aria-label={ariaLabel}
        aria-multiline="true"
        className={`richTextEditor ${className}`.trim()}
        contentEditable={!disabled}
        data-placeholder={placeholder}
        onInput={emitChange}
        onPaste={handlePaste}
        ref={editorRef}
        role="textbox"
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;
